<template>
  <section class="craps-panel" aria-labelledby="my-wagers-title">
    <div class="craps-row"><h2 id="my-wagers-title">Your wagers</h2><span class="craps-muted">{{ personal.wagers.length }} active positions</span></div>
    <p class="craps-note">Each bet has its own ID. Taking down an eligible line bet also returns its extra odds stake. A new total replaces the stake; Press adds up to that bet’s latest unpressed win.</p>
    <button v-if="removableIds.length" class="craps-button" :disabled="disabled" @click="$emit('action', { type: 'clear', wagerIds: removableIds })">Take down all removable wagers</button>
    <p v-if="!personal.wagers.length" class="craps-muted">Your accepted wagers will appear here, including bets placed in chat.</p>
    <article v-for="wager in personal.wagers" :key="wager.id" class="craps-wager" :data-wager-id="wager.id">
      <div class="craps-wager__heading">
        <strong>{{ label(wager.type) }}{{ wager.number ? ' · ' + wager.number : '' }}{{ wager.faces ? ' · ' + wager.faces.join(' + ') : '' }}</strong>
        <span>{{ formatSwiftBucks(wager.amount) }} SB reserved</span>
        <span class="craps-wager__working">{{ working(wager) ? 'Working' : 'Off' }} · {{ wager.working || 'default' }}</span>
      </div>
      <p class="craps-note">Bet #{{ wager.id }}<span v-if="wager.parentId"> · Attached to bet #{{ wager.parentId }}</span> · {{ wager.rulesVersion }}</p>
      <div class="craps-row">
        <button class="craps-button" :disabled="disabled || !legal('remove', wager)" :aria-label="'Take down wager ' + wager.id" @click="act('remove', wager)">Take down</button>
        <button class="craps-button" :disabled="disabled || !canPress(wager)" :aria-label="'Press wager ' + wager.id" @click="act('press', wager)">Press win ({{ formatSwiftBucks(wager.lastWinProfit) }})</button>
        <button v-if="oddsType(wager)" class="craps-button" :disabled="disabled" :aria-label="'Add odds to wager ' + wager.id" @click="$emit('target', { type: oddsType(wager), parentId: wager.id, number: wager.number })">Add odds</button>
        <label v-if="definition(wager).toggle" class="craps-label">Working setting
          <select class="craps-input" :value="wager.working || 'default'" :disabled="disabled" :aria-label="'Working setting for wager ' + wager.id" @change="$emit('action', { type: 'working', wagerId: wager.id, working: $event.target.value })">
            <option value="default">Table default</option><option value="on">On</option><option value="off">Off</option>
          </select>
        </label>
      </div>
      <details v-if="adjustable(wager)" class="craps-wager__adjust">
        <summary>Adjust this position</summary>
        <div class="craps-row">
          <label class="craps-label">New total stake<input class="craps-input" :aria-label="'New total for wager ' + wager.id" :value="totals[wager.id] || ''" placeholder="Amount or 10%" @input="$set(totals, wager.id, $event.target.value)"></label>
          <button class="craps-button" :disabled="disabled || !legal('increase', wager, totals[wager.id])" @click="act('increase', wager, totals[wager.id])">Increase</button>
          <button class="craps-button" :disabled="disabled || !legal('reduce', wager, totals[wager.id])" @click="act('reduce', wager, totals[wager.id])">Reduce</button>
        </div>
      </details>
      <p v-if="!adjustable(wager) && !definition(wager).toggle" class="craps-note">{{ definition(wager).family === 'bonus' ? 'Side bets stay committed until their result.' : 'This established contract stays until its result.' }}</p>
    </article>
  </section>
</template>
<script>
import { formatSwiftBucks } from '../../lib/format-swiftbucks'
export default {
  props: { personal: Object, table: Object, api: Object, disabled: Boolean },
  data () { return { totals: {} } },
  computed: {
    removableIds () { return this.personal.wagers.filter(wager => this.legal('remove', wager)).map(wager => wager.id) },
    ruleState () { const hand = this.table.hand || {}; return { point: hand.point || null, rollCount: Number(hand.rollCount || 0) } },
    owned () { return this.personal.wagers.map(wager => Object.assign({}, wager, { userId: this.personal.owner })) }
  },
  methods: {
    formatSwiftBucks,
    label (type) { return type.replace(/DONT/g, "DON'T").replace(/_/g, ' ') },
    definition (wager) { return this.api ? this.api.rulebook.CATALOGUE[wager.type] : {} },
    working (wager) { return this.api ? this.api.rulebook.isWorking(wager, this.ruleState) : wager.working !== 'off' },
    legal (action, wager, amount) {
      if (!this.api) return false
      try {
        let normalized
        if (amount) normalized = this.api.rulebook.normalizeWager(wager, this.api.exactAmounts.budget(amount, this.personal.balance.spendable)).wager.amount
        this.api.rulebook.validateManagement(action, Object.assign({}, wager, { userId: this.personal.owner }), this.ruleState, { amount: normalized, wagers: this.owned })
        return true
      } catch (_) { return false }
    },
    adjustable (wager) { return this.legal('remove', wager) },
    canPress (wager) {
      if (!this.api || !wager.lastWinProfit || wager.lastWinProfit === '0') return false
      try {
        const increment = this.api.rulebook.normalizeWager(wager, wager.lastWinProfit).wager.amount
        return this.legal('increase', wager, this.api.exactAmounts.add(wager.amount, increment))
      } catch (_) { return false }
    },
    oddsType (wager) {
      return wager.number && ['PASS', 'DONT_PASS', 'COME', 'DONT_COME'].indexOf(wager.type) !== -1 ? wager.type + '_ODDS' : null
    },
    act (type, wager, amount) {
      const intent = { type, wagerId: wager.id }
      if (amount) intent.amount = amount
      this.$emit('action', intent)
    }
  }
}
</script>
<style scoped>
h2 { font-size: 22px; margin: 0; }
.craps-wager { border-top: 1px solid #3a3a47; padding: 18px 0; margin-top: 16px; }
.craps-wager:last-child { padding-bottom: 0; }
.craps-wager__heading { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
.craps-wager__heading strong { color: #ffcc91; }
.craps-wager__working { font-size: 13px; border: 1px solid #696472; padding: 3px 8px; border-radius: 5px; }
.craps-wager__adjust { margin-top: 15px; }
summary { cursor: pointer; padding: 5px 0; }
</style>



// WEBPACK FOOTER //
// src/components/craps/Craps-Wagers.vue